import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { ThemeProvider } from 'styled-components';
import { ConfigProvider, Space } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import moment from 'moment';
import { AppDispatch, RootState } from '../../../redux/store';
import { fetchStores } from './store/weeklyReportsSlice';
import { useTheme } from '../../../context/ThemeContext';
import { getAntdThemeConfig } from '../../../utils/antdTheme';
import DateRangePicker from '../../../commonComponents/DateRangePicker';
import CustomersDropDown from '../../Loyalty/CustomersDropdown';
import StoresDropDown from '../../Analytics/StoresDropDown';
import {
  Container,
  HeaderSection,
  HeaderLeft,
  HeaderRight,
  TitleSection,
  MainTitle,
  Subtitle,
  BackButton,
  FilterSection,
  FilterLabel,
} from './Styles';
import WeeklyReportSummary from './WeeklyReportSummary';
import DailyBreakdownSection from './DailyBreakdownSection';
import CustomerStatsSection from './CustomerStatsSection';
import CategorySalesSection from './CategorySalesSection';
import ItemSalesSection from './ItemSalesSection';
import { Merchant, DateRange } from './types';

const WeeklyReports: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();
  const { reportData, loading } = useSelector(
    (state: RootState) => state.weeklyReports
  );

  const [merchant, setMerchant] = useState<Merchant | null>(null);
  
  const [selectedDateRange, setSelectedDateRange] = useState<DateRange>(() => ({
    startDate: moment().startOf('week'),
    endDate: moment().endOf('week'),
  }));
  const [storeId, setStoreId] = useState<string>('');
  
  const storesFetchedRef = useRef(false);
  const storesFetchingRef = useRef(false);
  const merchantInitializedRef = useRef(false);

  const user = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem('user') || '{}');
    } catch {
      return {};
    }
  }, []);

  const userRole = useMemo(() => user?.role, [user?.role]);

  const theme = { mode: (isDarkMode ? 'dark' : 'light') as 'light' | 'dark' };
  const antdTheme = getAntdThemeConfig(isDarkMode);

  useEffect(() => {
    if (userRole === 'merchant' && !merchant && !merchantInitializedRef.current) {
      setMerchant(user);
      merchantInitializedRef.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userRole]);

  useEffect(() => {
    if (storesFetchedRef.current) return;
    if (storesFetchingRef.current) return;
    if (userRole === 'admin') {
      storesFetchedRef.current = true;
      return;
    }
    if (storeId) {
      storesFetchedRef.current = true;
      return;
    }
    
    storesFetchedRef.current = true;
    storesFetchingRef.current = true;
    
    const fetchUserStores = async () => {
      try {
        const result = await dispatch(fetchStores());
        if (fetchStores.fulfilled.match(result)) {
          const stores = result.payload;
          if (stores.length > 0) {
            setStoreId(stores[0].id);
          }
        }
      } catch (error) {
        console.error('Error fetching user stores:', error);
        storesFetchedRef.current = false;
      } finally {
        storesFetchingRef.current = false;
      }
    };

    fetchUserStores();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selectedDateRangeRef = useRef<DateRange>(selectedDateRange);
  
  useEffect(() => {
    selectedDateRangeRef.current = selectedDateRange;
  }, [selectedDateRange]);

  const handleDateRangeChange = useCallback((range: DateRange) => {
    const current = selectedDateRangeRef.current;
    const currentStart = current.startDate
      ? moment(current.startDate).format('YYYY-MM-DD')
      : null;
    const currentEnd = current.endDate
      ? moment(current.endDate).format('YYYY-MM-DD')
      : null;
    
    const newStart = range.startDate
      ? moment(range.startDate).format('YYYY-MM-DD')
      : null;
    const newEnd = range.endDate
      ? moment(range.endDate).format('YYYY-MM-DD')
      : null;
    
    if (currentStart !== newStart || currentEnd !== newEnd) {
      setSelectedDateRange(range);
    }
  }, []);

  return (
    <ThemeProvider theme={theme}>
      <ConfigProvider theme={antdTheme}>
        <Container>
          <HeaderSection>
            <HeaderLeft>
              <BackButton
                icon={<ArrowLeftOutlined />}
                onClick={() => navigate(-1)}
                aria-label="Go Back to Reports Dashboard"
              />
              <TitleSection>
                <MainTitle level={2}>Weekly Reports</MainTitle>
                <Subtitle>
                  View detailed weekly sales and performance reports
                </Subtitle>
              </TitleSection>
            </HeaderLeft>
            <HeaderRight>
              <FilterSection size="middle" wrap>
                {userRole === 'admin' && (
                  <Space>
                    <FilterLabel>By Merchants</FilterLabel>
                    <CustomersDropDown
                      onOptionSelect={(merch: Merchant) => {
                        setMerchant(merch);
                      }}
                    />
                  </Space>
                )}
                <Space>
                  <FilterLabel>By Store</FilterLabel>
                  <StoresDropDown
                    onOptionSelect={(store: { id: string }) => {
                      setStoreId(store.id);
                    }}
                    merchantId={merchant?.id}
                    role={userRole}
                  />
                </Space>
                <Space>
                  <FilterLabel>Week</FilterLabel>
                  <DateRangePicker
                    selectedDateRange={selectedDateRange}
                    onDateRangeChange={handleDateRangeChange}
                  />
                </Space>
              </FilterSection>
            </HeaderRight>
          </HeaderSection>

          <WeeklyReportSummary
            merchant={merchant}
            storeId={storeId}
            selectedDateRange={selectedDateRange}
          />

          <DailyBreakdownSection reportData={reportData} loading={loading} />
          <CustomerStatsSection reportData={reportData} loading={loading} />
          <CategorySalesSection reportData={reportData} loading={loading} />
          <ItemSalesSection reportData={reportData} loading={loading} />
        </Container>
      </ConfigProvider>
    </ThemeProvider>
  );
};

export default WeeklyReports;
